import type {
  MarketPrice,
  NewSealedProduct,
  SealedProduct,
  SealedProductListFilter,
  SealedStockUpdate,
} from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { idleMarketRefresh, type MarketRefreshStatus } from "../../core/primitives/catalog-market";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";

export const SEALED_FILTERS: { key: SealedProductListFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "in-stock", label: "In stock" },
  { key: "archived", label: "Archived" },
];

/** The dialog that is open: adding a product, or counting, archiving or pricing one. */
export type SealedDialog =
  | { readonly kind: "add" }
  | { readonly kind: "stock" | "archive" | "market"; readonly product: SealedProduct };

export class SealedState {
  readonly filter = new Rx<SealedProductListFilter>("all");
  readonly search = new Rx("");
  readonly status = new Rx<LoadStatus>(idleLoad);
  /** Whether the shop's list has answered once: a read after that keeps what the page shows, the empty list too. */
  readonly answered = new Rx(false);
  readonly items = new RxList<SealedProduct>();
  /** The rows a save holds until a list read asked after it has landed. */
  readonly held = new Rx<ReadonlySet<string>>(new Set());
  readonly dialog = new Rx<SealedDialog | null>(null);
  readonly dialogBusy = new Rx(false);
  /** After a save that threw, its Save waits for the list read that says whether it went through. */
  readonly dialogChecking = new Rx(false);
  readonly dialogError = new Rx<string | null>(null);
  /** The open market dialog's estimate and shared history. */
  readonly market = new Rx<MarketPrice | null>(null);
  readonly marketStatus = new Rx<LoadStatus>(idleLoad);
  readonly marketRefresh = new Rx<MarketRefreshStatus>(idleMarketRefresh);
  /** What waits on Review, for Inventory's switch. */
  readonly reviewCount = new Rx(0);
}

export abstract class SealedActions {
  abstract setFilter(filter: SealedProductListFilter): void;
  abstract setSearch(search: string): void;
  abstract openAdd(): void;
  abstract retry(): void;
  abstract openStock(product: SealedProduct): void;
  abstract openArchive(product: SealedProduct): void;
  /** The shared market estimate and its history, the same in every shop. */
  abstract openMarket(product: SealedProduct): Promise<void>;
  /** Asks the source for the estimate; the shop's own prices stay. */
  abstract refreshMarket(): Promise<void>;
  abstract closeDialog(): void;
  abstract saveNew(product: NewSealedProduct): Promise<void>;
  abstract saveStock(change: SealedStockUpdate): Promise<void>;
  abstract confirmArchive(): Promise<void>;
}
